import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, Loader2, BarChart3, Users, Package, DollarSign,
  Settings as SettingsIcon, Download as DownloadIcon, RefreshCw, Send,
  Sparkles, Repeat, Eye, EyeOff, PowerOff,
} from "lucide-react";
import ProductForm from "@/components/admin/ProductForm";
import SubscribersTab from "@/components/admin/SubscribersTab";
import AppPromoSlidesAdmin from "@/components/app-landing/AppPromoSlidesAdmin";
import MarketplaceAdmin from "@/components/admin/marketplace/MarketplaceAdmin";
import AttendanceAdmin from "@/components/admin/AttendanceAdmin";
import ProjectionAdmin from "@/components/admin/projection/ProjectionAdmin";

const CATEGORIES = {
  music: ["Worship Songs", "Praise Songs", "Morning Devotion", "Wedding Songs", "Mother's Day Songs", "Father's Day Songs", "Motivation & Inspiration", "Children's Gospel Songs"],
  template: ["HR Analytics", "Finance Dashboard", "Sales Dashboard", "Healthcare Analytics", "Project Management Dashboard", "Church/Admin Dashboard", "Executive KPI Dashboard"],
  ebook: ["Inspirational Books", "Music Guides", "Business & Data", "Devotionals"],
  merch: ["T-Shirts", "Hoodies", "Caps", "Mugs", "Posters", "Tote Bags", "Stickers"],
};

const empty = {
  title: "", type: "music", category: CATEGORIES.music[0], description: "",
  price_mp3: 1.99, price_wav: 4.99, price: 29,
  cover_image_url: "", preview_audio_url: "", demo_video_url: "",
  file_mp3_url: "", file_wav_url: "", file_template_url: "", file_ebook_url: "",
  file_type: "", whats_included: [], featured: false,
  status: "online",
  sizes: [], colors: [], stock_status: "in_stock", external_url: "",
};

const STATUS_STYLES = {
  online:  "border-emerald-500/40 text-emerald-300 bg-emerald-900/20",
  offline: "border-amber-500/40 text-amber-300 bg-amber-900/20",
  draft:   "border-neutral-500/40 text-neutral-400 bg-neutral-900/40",
};

function StatusBadge({ status }) {
  const s = status || "online";
  const cls = STATUS_STYLES[s] || STATUS_STYLES.online;
  return (
    <span data-testid={`status-badge-${s}`} className={`inline-block px-2 py-0.5 text-[9px] tracking-widest uppercase rounded border ${cls}`}>
      {s}
    </span>
  );
}

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [segments, setSegments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [settings, setSettings] = useState({});
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/admin/login"); return; }
    refresh();
    // eslint-disable-next-line
  }, [user, authLoading]);

  const refresh = async () => {
    try {
      const [p, o, c, a, s, st] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/orders"),
        api.get("/admin/customers"),
        api.get("/admin/analytics"),
        api.get("/admin/segments"),
        api.get("/admin/settings"),
      ]);
      setProducts(p.data); setOrders(o.data); setCustomers(c.data); setAnalytics(a.data); setSegments(s.data); setSettings(st.data || {});
    } catch (e) { console.error(e); }
  };

  const exportCustomers = async () => {
    try {
      const res = await fetch(`${API}/admin/customers/export`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "hoh_vibes_customers.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Customer CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const syncMailchimp = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/customers/sync");
      toast.success(`Synced ${data.synced}/${data.total} orders to Mailchimp`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/admin/products/${form.id}`, stripId(form));
      else await api.post("/admin/products", stripId(form));
      toast.success("Saved");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const stripId = (f) => { const { id, created_at, status_updated_at, ...rest } = f; return rest; };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); refresh(); }
    catch { toast.error("Delete failed"); }
  };

  const setStatus = async (p, next) => {
    try {
      await api.patch(`/admin/products/${p.id}/status`, { status: next });
      toast.success(`${p.title} → ${next}`);
      setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, status: next } : x));
    } catch (e) { toast.error(e?.response?.data?.detail || "Status update failed"); }
  };

  const toggleOnline = (p) => {
    const next = (p.status || "online") === "online" ? "offline" : "online";
    setStatus(p, next);
  };

  const preview = (p) => {
    const url = `/product/${p.id}?preview=admin`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const updateShipping = async (orderId, status) => {
    try { await api.patch(`/admin/orders/${orderId}/shipping`, { shipping_status: status }); toast.success("Shipping updated"); refresh(); }
    catch { toast.error("Update failed"); }
  };

  if (authLoading) return <div className="max-w-7xl mx-auto px-6 py-20"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;

  const filtered = products.filter((p) =>
    (filterType === "all" || p.type === filterType) &&
    (filterStatus === "all" || (p.status || "online") === filterStatus)
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="admin-dashboard">
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="label-eyebrow mb-3">Admin Console</div>
          <h1 className="font-serif-display text-5xl text-white">HOH_Vibes <span className="italic text-[#D4AF37]">Studio</span></h1>
        </div>
        <Link to="/admin/settings" data-testid="admin-settings-link" className="btn-ghost-gold px-4 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2">
          <SettingsIcon size={14} /> Settings
        </Link>
      </div>

      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mt-10" data-testid="admin-analytics">
          <Stat icon={DollarSign} label="Revenue" value={`£${analytics.total_revenue.toFixed(2)}`} />
          <Stat icon={Package} label="Paid Orders" value={analytics.paid_orders} />
          <Stat icon={BarChart3} label="Products" value={analytics.products} />
          <Stat icon={Users} label="Customers" value={analytics.customers} />
          <Stat icon={Repeat} label="Repeat Rate" value={`${analytics.repeat_purchase_rate || 0}%`} />
          <Stat icon={Sparkles} label="Avg CLV" value={`£${(analytics.avg_clv || 0).toFixed(2)}`} />
        </div>
      )}

      <div className="flex gap-2 mt-10 border-b border-white/10 overflow-x-auto">
        {[
          ["overview", "Products"],
          ["orders", "Orders"],
          ["customers", "Customers"],
          ["subscribers", "Subscribers"],
          ["segments", "Segments"],
          ["app_landing", "App Landing"],
          ["marketplace", "Marketplace"],
          ["attendance", "Attendance"],
          ["projection", "Worship Projection"],
        ].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-3 text-xs tracking-widest uppercase border-b-2 whitespace-nowrap ${tab === k ? "text-[#D4AF37] border-[#D4AF37]" : "text-neutral-400 border-transparent hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="mt-8">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
            <div className="flex gap-2 flex-wrap items-center">
              {["all", "music", "template", "ebook", "merch"].map((t) => (
                <button key={t} data-testid={`filter-${t}`} onClick={() => setFilterType(t)}
                  className={`px-3 py-1.5 text-[10px] tracking-widest uppercase rounded border ${filterType === t ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-neutral-300"}`}>{t}</button>
              ))}
            </div>
            <button data-testid="admin-add-product" onClick={() => setEditing({ ...empty })} className="btn-gold px-4 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2">
              <Plus size={14} /> New Product
            </button>
          </div>
          {/* Status filter row */}
          <div className="flex flex-wrap gap-2 mb-6 items-center">
            <span className="text-[10px] tracking-widest text-neutral-500 uppercase mr-2">Status</span>
            {["all", "online", "offline", "draft"].map((s) => (
              <button key={s} data-testid={`filter-status-${s}`} onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 text-[10px] tracking-widest uppercase rounded border ${filterStatus === s ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-neutral-300"}`}>{s}</button>
            ))}
            <div className="text-xs text-neutral-500 ml-3">{filtered.length} product{filtered.length === 1 ? "" : "s"}</div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-xs tracking-widest uppercase text-neutral-400">
                  <th className="text-left py-3">Title</th>
                  <th className="text-left">Type</th>
                  <th className="text-left">Category</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Price</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const isOnline = (p.status || "online") === "online";
                  return (
                    <tr key={p.id} data-testid={`product-row-${p.id}`} className="border-b border-white/5">
                      <td className="py-3 text-white">{p.title}{p.featured && <span className="ml-2 text-[10px] text-[#D4AF37]">★</span>}</td>
                      <td className="text-neutral-400">{p.type}</td>
                      <td className="text-neutral-400">{p.category}</td>
                      <td><StatusBadge status={p.status} /></td>
                      <td className="text-[#D4AF37]">{p.type === "music" ? `£${p.price_mp3} / £${p.price_wav}` : `£${p.price}`}</td>
                      <td className="text-right whitespace-nowrap">
                        <button data-testid={`toggle-${p.id}`} title={isOnline ? "Take offline" : "Take online"} onClick={() => toggleOnline(p)}
                          className={`p-2 ${isOnline ? "text-emerald-400 hover:text-emerald-200" : "text-amber-400 hover:text-amber-200"}`}>
                          {isOnline ? <PowerOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button data-testid={`set-draft-${p.id}`} title="Set as Draft" onClick={() => setStatus(p, "draft")}
                          className={`p-2 ${(p.status || "online") === "draft" ? "text-[#D4AF37]" : "text-neutral-500 hover:text-neutral-200"}`}>
                          <EyeOff size={14} />
                        </button>
                        <button data-testid={`preview-${p.id}`} title="Preview" onClick={() => preview(p)} className="text-neutral-400 hover:text-[#D4AF37] p-2">
                          <Eye size={14} />
                        </button>
                        <button data-testid={`edit-${p.id}`} title="Edit" onClick={() => setEditing(p)} className="text-neutral-400 hover:text-[#D4AF37] p-2"><Pencil size={14} /></button>
                        <button data-testid={`delete-${p.id}`} title="Delete" onClick={() => remove(p.id)} className="text-neutral-400 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={6} className="py-6 text-neutral-500 text-center">No products in this view.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-8 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10 text-xs tracking-widest uppercase text-neutral-400">
              <th className="text-left py-3">Order</th><th>Email</th><th>Items</th><th>Total</th><th>Payment</th><th>Shipping</th><th>Date</th>
            </tr></thead>
            <tbody>
              {orders.map((o) => {
                const sym = (o.currency || "gbp").toLowerCase() === "usd" ? "$" : "£";
                return (
                  <tr key={o.id} className="border-b border-white/5">
                    <td className="py-3 text-white">#{o.id.slice(0, 8)}</td>
                    <td className="text-neutral-300">{o.email}</td>
                    <td className="text-neutral-400">{o.items?.length || 0}</td>
                    <td className="text-[#D4AF37]">{sym}{Number(o.amount).toFixed(2)}</td>
                    <td><span className={`text-xs uppercase tracking-widest ${o.payment_status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>{o.payment_status}</span></td>
                    <td>
                      {o.has_merch ? (
                        <select data-testid={`shipping-${o.id}`} value={o.shipping_status || "pending"} onChange={(e) => updateShipping(o.id, e.target.value)} className="bg-black border border-white/15 text-white text-xs px-2 py-1 rounded">
                          <option value="pending">Pending</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      ) : <span className="text-xs text-neutral-500">— digital —</span>}
                    </td>
                    <td className="text-neutral-500 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                  </tr>
                );
              })}
              {!orders.length && <tr><td colSpan={7} className="py-6 text-neutral-500 text-center">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "customers" && (
        <div className="mt-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="text-sm text-neutral-400">{customers.length} customers</div>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="export-customers" onClick={exportCustomers} className="btn-ghost-gold px-3 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2">
                <DownloadIcon size={14} /> Export CSV
              </button>
              <button data-testid="sync-mailchimp" onClick={syncMailchimp} disabled={syncing} className="btn-gold px-3 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2">
                {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Sync Mailchimp
              </button>
              {settings.mailchimp_campaign_url && (
                <a data-testid="send-campaign" href={settings.mailchimp_campaign_url} target="_blank" rel="noreferrer" className="btn-ghost-gold px-3 py-2 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2">
                  <Send size={14} /> Send Campaign
                </a>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-xs tracking-widest uppercase text-neutral-400">
                <th className="text-left py-3">Name</th><th className="text-left">Email</th><th className="text-right">Orders</th><th className="text-right">Spend</th><th>Tags</th><th className="text-right">Joined</th>
              </tr></thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-b border-white/5">
                    <td className="py-3 text-white">{c.name}</td>
                    <td className="text-neutral-300">{c.email}</td>
                    <td className="text-right text-neutral-300">{c.total_orders || 0}</td>
                    <td className="text-right text-[#D4AF37]">£{Number(c.total_spent || 0).toFixed(2)}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).slice(0, 3).map((t) => (
                          <span key={t} className="px-2 py-0.5 text-[9px] tracking-widest uppercase rounded border border-[#D4AF37]/30 text-[#D4AF37]">{t}</span>
                        ))}
                        {(c.tags || []).length > 3 && <span className="text-[10px] text-neutral-500">+{c.tags.length - 3}</span>}
                      </div>
                    </td>
                    <td className="text-neutral-500 text-xs text-right">{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
                {!customers.length && <tr><td colSpan={6} className="py-6 text-neutral-500 text-center">No customers yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "subscribers" && <SubscribersTab />}

      {tab === "marketplace" && <MarketplaceAdmin />}

      {tab === "attendance" && <AttendanceAdmin />}
      {tab === "projection" && <ProjectionAdmin />}

      {tab === "app_landing" && (
        <div className="mt-8" data-testid="app-landing-tab">
          <AppPromoSlidesAdmin />
        </div>
      )}

      {tab === "segments" && (
        <div className="mt-8" data-testid="segments-tab">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="text-sm text-neutral-400">{segments.length} segments</div>
            {settings.mailchimp_auto_sync ? (
              <span className="text-[10px] tracking-widest uppercase text-emerald-400">● Mailchimp auto-sync ON</span>
            ) : (
              <span className="text-[10px] tracking-widest uppercase text-amber-400">● Auto-sync OFF — enable in Settings</span>
            )}
          </div>
          {segments.length === 0 ? (
            <div className="luxury-card rounded-xl p-8 text-center">
              <Sparkles size={28} className="text-[#D4AF37] mx-auto mb-3" />
              <p className="text-neutral-400">No segments yet. Segments are created automatically when customers complete paid orders.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((s) => {
                const tpl = settings.mailchimp_campaign_url || "";
                const href = tpl.includes("{tag}")
                  ? tpl.replace(/\{tag\}/g, encodeURIComponent(s.tag))
                  : tpl ? `${tpl}${tpl.includes("?") ? "&" : "?"}tag=${encodeURIComponent(s.tag)}` : "";
                return (
                  <div key={s.tag} data-testid={`segment-${s.tag}`} className="luxury-card rounded-xl p-5 flex flex-col">
                    <div className="label-eyebrow">{s.tag}</div>
                    <div className="flex items-end justify-between mt-3">
                      <div>
                        <div className="text-[10px] tracking-widest uppercase text-neutral-500">Customers</div>
                        <div className="font-serif-display text-3xl text-white">{s.customers}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] tracking-widest uppercase text-neutral-500">Revenue</div>
                        <div className="font-serif-display text-xl text-[#D4AF37]">£{Number(s.revenue).toFixed(2)}</div>
                      </div>
                    </div>
                    {href ? (
                      <a href={href} target="_blank" rel="noreferrer noopener"
                        data-testid={`send-to-tag-${s.tag}`}
                        className="btn-gold mt-4 inline-flex items-center justify-center gap-2 px-3 py-2 rounded text-[10px] tracking-widest uppercase font-semibold">
                        <Send size={12} /> Send to {s.tag}
                      </a>
                    ) : (
                      <div className="mt-4 text-[10px] text-neutral-500 tracking-widest uppercase">
                        Add Mailchimp Campaign URL in Settings to enable
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {editing && <ProductForm form={editing} setForm={setEditing} onSave={save} onClose={() => setEditing(null)} categories={CATEGORIES} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="luxury-card rounded-xl p-5">
      <Icon size={18} className="text-[#D4AF37]" />
      <div className="text-xs tracking-widest uppercase text-neutral-500 mt-3">{label}</div>
      <div className="font-serif-display text-3xl text-white mt-1">{value}</div>
    </div>
  );
}
